import axios from 'axios'
import { track } from './progress'

/**
 * Relative base URL (so staging talks to staging). The sign-in is NOT here: it lives in
 * a cookie no page script can read (server: utils/staffCookie.js), which the browser
 * sends by itself. X-Requested-With is what lets the server accept a changing request
 * made with that cookie; X-LQ-Cookie asks it not to hand the token back at sign-in.
 */
export const TOKEN_KEY = 'dashboard_jwt'

// The previous version kept the sign-in in localStorage. Take it out: nothing reads it now.
try { localStorage.removeItem(TOKEN_KEY) } catch { /* private mode */ }

export const api = axios.create({
  baseURL: '/api/v1/dashboard',
  withCredentials: true,
  headers: { 'X-Requested-With': 'lynqpod', 'X-LQ-Cookie': '1' },
})
// Requests that follow a click move the progress bar along the top (components/ProgressBar).
track(api)

/*
 * A SESSION THAT HAS GONE SENDS YOU TO SIGN IN, once.
 *
 * Before this, an expired session left every panel on the page showing "Unauthorized" and no
 * way forward but a manual reload. A 401 now means the sign-in is over: go to the login page
 * and say so. The sign-in page itself is left alone, or a wrong password would bounce the
 * page instead of showing the error, and the flag stops a screenful of failing requests
 * turning into a screenful of redirects.
 */
/*
 * ⚠️ PAGES A STRANGER IS MEANT TO SEE MUST NEVER BE BOUNCED.
 *
 * Every page load asks /auth/me, and for somebody who is not signed in a 401 is the correct
 * answer, not an expired session. On a page that requires a sign-in that distinction does not
 * matter; on the public ones it does, and it broke them: a patient opening a booking link was
 * sent to a staff login screen reading "your session had been open a while", which they could
 * do nothing about. The booking page, the patient portal and the form links are public on
 * purpose and are the pages a practice actually hands to the outside world.
 *
 * Kept in step with the same list in App.tsx (useConsoleForwarding).
 */
const PUBLIC_PATHS = ['/login', '/platform', '/book', '/p/', '/f/', '/form', '/portal']
const onPublicPage = () =>
  typeof window !== 'undefined'
  && PUBLIC_PATHS.some((p) => window.location.pathname === p || window.location.pathname.startsWith(p.endsWith('/') ? p : p + '/') || window.location.pathname === p)

/* The sign-in probe itself: a 401 here is an answer, never a session that has run out. */
const isAuthProbe = (url?: string) => !!url && url.replace(/\?.*$/, '').endsWith('/auth/me')

let bouncing = false
api.interceptors.response.use(
  (r) => r,
  (e) => {
    const onLogin = typeof window !== 'undefined' && window.location.pathname.startsWith('/login')
    if (axios.isAxiosError(e) && e.response?.status === 401
        && !onLogin && !onPublicPage() && !isAuthProbe(e.config?.url) && !bouncing) {
      bouncing = true
      const back = window.location.pathname + window.location.search
      window.location.assign(`/login?expired=1&next=${encodeURIComponent(back)}`)
    }
    return Promise.reject(e)
  },
)

/** Turn an axios failure into something worth showing a user. */
/**
 * True when the server said this practice has not been set up yet, rather than that
 * something failed. A newly provisioned practice has no charges or commission rates, and
 * every finance page asks for an invoice the moment somebody signs in: that is an ordinary
 * state to be explained, not a red banner.
 */
export function isSetupNeeded(e: unknown): boolean {
  return axios.isAxiosError(e) && e.response?.status === 409
    && !!(e.response?.data as { setupNeeded?: boolean } | undefined)?.setupNeeded
}

export function errorMessage(e: unknown, fallback = 'Something went wrong.'): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: string; message?: string } | undefined
    /*
     * 401 FIRST. The server says "Unauthorized" or "Invalid or expired token", and showing
     * that to somebody mid-payroll reads like they did something wrong rather than that their
     * session ran out. The server's own wording still wins for every other status.
     */
    if (e.response?.status === 401) return 'Your session has expired. Please sign in again.'
    if (data?.error) return data.error
    if (data?.message) return data.message
    if (e.response?.status === 403) return 'Your account does not have access to this.'
    if (e.response) return `Request failed (HTTP ${e.response.status}).`
    return 'Could not reach the server.'
  }
  return e instanceof Error ? e.message : fallback
}

/**
 * POST a body and save the response as a file.
 *
 * The endpoints return the file itself rather than a URL, so the download is built
 * from the blob. `responseType: 'blob'` also means an ERROR body arrives as a blob,
 * not JSON — read it back as text before reporting, or the user gets
 * "[object Blob]" where the reason should be.
 */
export async function downloadPost(url: string, body: unknown, fallbackName: string): Promise<void> {
  try {
    const r = await api.post(url, body, { responseType: 'blob' })
    const cd = String(r.headers['content-disposition'] ?? '')
    const name = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? fallbackName
    const href = URL.createObjectURL(r.data as Blob)
    const a = document.createElement('a')
    a.href = href; a.download = name
    document.body.appendChild(a); a.click(); a.remove()
    // Revoked on the next tick: revoking immediately can cancel the download in Safari.
    setTimeout(() => URL.revokeObjectURL(href), 10_000)
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.data instanceof Blob) {
      const text = await e.response.data.text()
      try { throw new Error(JSON.parse(text).error ?? text) } catch (parsed) {
        throw parsed instanceof Error ? parsed : new Error(text)
      }
    }
    throw e
  }
}
